import { ReactNode, useEffect, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDownIcon, FolderOpenIcon, PlusIcon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    DropdownMenuItem,
    GridLoadingState,
    PageTitle,
    SourceCard,
    SourceCardSkeleton,
} from '@/components';
import { Button } from '@/components/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useInfiniteScroll, usePermissions } from '@/hooks';
import { Permission } from '@/types/permissions';
import { FullSource, SourceType } from '@/types/source';
import { isHdmiViewport } from '@/utils/source';
import { EngineService, ThumbnailSize } from '@/services/engine';
import { stripFileExtension } from '@/utils/file-name';
import { usePipPlayerStore } from '@/stores/pip-player.store';
import { useNewSourceOptions } from '../hooks/useNewSourceOptions';
import { CARD_GRID_COLUMNS_CLASSES } from '@/constants/layout';
import { cn } from '@/lib/utils';

function getSourceThumbnail(source: FullSource): string {
    if (source.type === SourceType.FILE) {
        return EngineService.getSourceThumbnailUrl(source.id, ThumbnailSize.MQ);
    }
    return source.src || '';
}

function getSourceCardTitle(source: FullSource): string {
    return source.displayName ?? stripFileExtension(source.name);
}

export interface FolderConfig {
    headerTitle: string;
    headerIcon: ReactNode;
    headerAccessory?: ReactNode;
    currentFolderId: string | null;
    onManageFolders?: (sourceId: string) => void;
}

interface SourceGridResultsProps {
    folderConfig: FolderConfig;
    sources: FullSource[];
    isSourceSelected: (source: FullSource) => boolean;
    isMultiSelectMode: boolean;
    onToggleSource: (
        sourceId: string,
        event?: MouseEvent<HTMLDivElement>,
    ) => void;
    onEditSource?: (sourceId: string) => void;
    onReanalyzeSource: (sourceId: string) => void;
    onDeleteSource: (sourceId: string) => void;
    onLoadMore: () => void;
    isLoadingMore: boolean;
    hasMore: boolean;
    isInitialLoading: boolean;
    scrollContainer: HTMLDivElement | null;
    onAddSourceFile?: () => void;
    onAddSourceCamera?: () => void;
    isCameraLimitReached?: boolean;
}

export function SourceGridResults({
    folderConfig,
    sources,
    isSourceSelected,
    isMultiSelectMode,
    onToggleSource,
    onEditSource,
    onReanalyzeSource,
    onDeleteSource,
    onLoadMore,
    isLoadingMore,
    hasMore,
    isInitialLoading,
    scrollContainer,
    onAddSourceFile,
    onAddSourceCamera,
    isCameraLimitReached,
}: SourceGridResultsProps) {
    const {
        headerTitle,
        headerIcon,
        headerAccessory,
        currentFolderId,
        onManageFolders,
    } = folderConfig;
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { canPerform } = usePermissions();
    const { videoFile, liveCamera } = useNewSourceOptions();
    const refreshPipNavigationContext = usePipPlayerStore(
        (s) => s.refreshNavigationContext,
    );

    useInfiniteScroll(scrollContainer, {
        onLoadMore,
        isLoading: isLoadingMore,
        hasMore,
    });

    useEffect(() => {
        refreshPipNavigationContext({
            type: 'sources-page',
            items: sources.map((source) => ({ id: source.id })),
            canLoadMore: hasMore,
            loadMore: onLoadMore,
        });
    }, [hasMore, onLoadMore, refreshPipNavigationContext, sources]);

    return (
        <div className="flex min-w-0 flex-col">
            <div className="min-w-0 pt-4 pb-8">
                <div className="flex min-w-0 flex-row items-center gap-2">
                    <PageTitle
                        className="min-w-0 flex-1"
                        icon={headerIcon}
                        text={headerTitle}
                        allowRename={false}
                        actionsTriggerTestId="folder-context-menu-btn"
                    />
                    {headerAccessory}
                </div>
            </div>
            <div className="flex min-w-0 flex-col">
                {currentFolderId &&
                sources.length === 0 &&
                !isInitialLoading &&
                ((videoFile.enabled && onAddSourceFile) ||
                    (liveCamera.enabled && onAddSourceCamera)) ? (
                    <div className="flex min-h-[60vh] items-center justify-center">
                        <div className="flex flex-col items-center gap-8 w-[346px] max-w-full">
                            <div className="flex flex-col items-center gap-6 w-full">
                                <FolderOpenIcon
                                    className="size-10 shrink-0 text-grey-400"
                                    weight="regular"
                                />
                                <div className="flex flex-col items-center gap-2 w-full">
                                    <h3 className="text-lg font-medium leading-7 text-grey-200 text-center w-full">
                                        {t('folders.emptyTitle')}
                                    </h3>
                                    <p className="text-sm font-normal leading-5 text-grey-300 text-center w-full">
                                        {t('folders.emptyDescription')}
                                    </p>
                                </div>
                            </div>
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="primary"
                                        size="medium"
                                        leadingIcon={
                                            <PlusIcon className="size-4" />
                                        }
                                        trailingIcon={
                                            <CaretDownIcon className="size-4" />
                                        }
                                        data-testid="folder-empty-state-new-source"
                                    >
                                        {t('folders.addSource')}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="center"
                                    sideOffset={8}
                                    className="min-w-[250px]"
                                >
                                    <DropdownMenuGroup>
                                        {videoFile.enabled &&
                                            onAddSourceFile && (
                                                <DropdownMenuItem
                                                    icon={videoFile.icon}
                                                    label={t(
                                                        videoFile.labelKey,
                                                    )}
                                                    onClick={onAddSourceFile}
                                                />
                                            )}
                                        {liveCamera.enabled &&
                                            onAddSourceCamera && (
                                                <DropdownMenuItem
                                                    icon={liveCamera.icon}
                                                    label={t(
                                                        liveCamera.labelKey,
                                                    )}
                                                    onClick={
                                                        isCameraLimitReached
                                                            ? undefined
                                                            : onAddSourceCamera
                                                    }
                                                    disabled={
                                                        isCameraLimitReached
                                                    }
                                                />
                                            )}
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                ) : (
                    <GridLoadingState
                        isLoading={isInitialLoading}
                        isEmpty={sources.length === 0}
                        isLoadingMore={isLoadingMore}
                        isReachingEnd={!hasMore}
                        emptyMessage={t('noSourcesFound')}
                        loadingMoreMessage={t('loadingMore')}
                        hideEndMessage
                        skeletonComponent={<SourceCardSkeleton />}
                        skeletonCount={9}
                        gridClassName={cn(
                            'grid gap-4',
                            CARD_GRID_COLUMNS_CLASSES,
                        )}
                    >
                        {sources.map((source, index) => {
                            const isSelected = isSourceSelected(source);

                            return (
                                <SourceCard
                                    key={source.id}
                                    id={source.id}
                                    thumbnail={getSourceThumbnail(source)}
                                    name={getSourceCardTitle(source)}
                                    createdAt={source.createdAt}
                                    duration={source.duration}
                                    type={source.type}
                                    canEdit={canPerform(
                                        Permission.EDIT,
                                        source.isOwner,
                                    )}
                                    canDelete={canPerform(
                                        Permission.DELETE,
                                        source.isOwner,
                                    )}
                                    isOwner={source.isOwner}
                                    tags={[]}
                                    checked={
                                        isMultiSelectMode &&
                                        !isHdmiViewport(source.id)
                                            ? isSelected
                                            : undefined
                                    }
                                    onClick={onToggleSource}
                                    onEditSource={onEditSource}
                                    onReanalyzeSource={onReanalyzeSource}
                                    onDelete={onDeleteSource}
                                    onManageFolders={onManageFolders}
                                    analysisStatus={source.status}
                                    analysisProgress={source.progress}
                                    quickViewNavigationContext={{
                                        type: 'sources-page',
                                        items: sources.map((s) => ({
                                            id: s.id,
                                        })),
                                        currentIndex: index,
                                        canLoadMore: hasMore,
                                        loadMore: onLoadMore,
                                    }}
                                />
                            );
                        })}
                    </GridLoadingState>
                )}
            </div>
        </div>
    );
}
